import type { Regimen } from "../types.ts";
import { davidRegimens } from "./plan-david/index.ts";
import { rominaRegimens } from "./plan-romina/index.ts";
import { romina2Regimens } from "./plan-romina-2/index.ts";

export type PlanId = "plan-david" | "plan-romina" | "plan-romina-2";

export interface Plan {
  id: PlanId;
  name: string;
  regimens: Regimen[];
  /** Regimen that weekday presets fall back to when the user switches to this plan */
  defaultRegimenId: Regimen["id"];
}

export const planDavid: Plan = {
  id: "plan-david",
  name: "Plan David",
  regimens: davidRegimens,
  defaultRegimenId: "rest",
};

export const planRomina: Plan = {
  id: "plan-romina",
  name: "Plan Romina",
  regimens: rominaRegimens,
  defaultRegimenId: "romina-rest",
};

export const planRomina2: Plan = {
  id: "plan-romina-2",
  name: "Plan Romina Nutri 2",
  regimens: romina2Regimens,
  defaultRegimenId: "romina-nutri-2",
};

export const planRegistry: Record<PlanId, Plan> = {
  "plan-david": planDavid,
  "plan-romina": planRomina,
  "plan-romina-2": planRomina2,
};

export const availablePlans: Plan[] = [planDavid, planRomina, planRomina2];

/** True when `id` names a registered plan (safe for untrusted input). */
export function isPlanId(id: unknown): id is PlanId {
  return typeof id === "string" && Object.hasOwn(planRegistry, id);
}

/**
 * Every regimen of every plan. Regimen ids are unique across plans, so a day
 * saved under one plan keeps resolving after its owner switches plans.
 */
export const allRegimens: Regimen[] = Object.values(planRegistry).flatMap((p) => p.regimens);

/** Resolves a regimen id to its regimen (`findRegimen`, or a fixture in tests). */
export type RegimenLookup = (id: Regimen["id"]) => Regimen | undefined;

/** Resolve a day's regimen by id, regardless of which plan it belongs to. */
export function findRegimen(id: Regimen["id"] | null | undefined): Regimen | undefined {
  return id ? allRegimens.find((r) => r.id === id) : undefined;
}

/**
 * One-letter badge for a day's regimen: the regimen's `badge`, else the first
 * letter of its name (of the id when the regimen is unknown).
 */
export function regimenBadge(id: Regimen["id"]): string {
  const regimen = findRegimen(id);
  return regimen?.badge ?? (regimen?.name ?? id).charAt(0).toUpperCase();
}
